import {
  Chat,
  useTurns,
  useAgentActivity,
  useChatInterrupt,
  useChatStreamSelector,
  AskUserQuestionCard,
  extractText,
} from "@launch10/use-langgraph-stream";
import { Fragment } from "react";
import type {
  HumanInterrupt,
  AskUserQuestionArgs,
  AskUserQuestionOption,
  AskUserQuestionResponse,
} from "@launch10/use-langgraph-stream/humanInterrupt";
import { InboxActions } from "@launch10/use-langgraph-stream/humanInterrupt";
import { ThinkingIndicator } from "@components/shared/chat/ThinkingIndicator";
import { CompactingIndicator } from "@components/shared/chat/CompactingIndicator";
import { ChatErrorBanner } from "@components/shared/chat/ChatErrorBanner";
import type { Turn } from "@launch10/use-langgraph-stream";
import type { Artifact } from "@shared";
import { useBrainstormSelector, useBrainstormIsCompacting } from "@components/brainstorm/hooks";
import {
  ArtifactRenderer,
  assignArtifactsToMessages,
  keywordPresent,
  useChatArtifacts,
} from "@components/artifacts";

/**
 * Inline-renders an active `ask_user_question` interrupt as a choice card in
 * the brainstorm stream. Returns null when the graph isn't paused on one (or
 * is paused on a different interrupt kind). Lives inside Chat.Root, so the
 * interrupt hooks resolve against the brainstorm stream store.
 *
 * Reifies on submit like RecommendationChips: it OPTIMISTICALLY drops the
 * question (Houston) + the user's reply (you) into the transcript so they're
 * visible instantly while the agent works the next turn — `optimisticValues`
 * overlays them for the duration of the resume request. The backend tool
 * persists the same Q/A pair (in the correct tool-call order), so when real
 * state arrives it replaces the overlay with no duplicate.
 */
function ActiveAskUserQuestion() {
  const interrupt = useChatInterrupt<HumanInterrupt>();
  const submit = useChatStreamSelector((s) => s.submit) as (
    input: null,
    options: Record<string, unknown>
  ) => Promise<void>;
  if (!interrupt || interrupt.action_request?.action !== InboxActions.ASK_USER_QUESTION) {
    return null;
  }

  const args = interrupt.action_request.args as AskUserQuestionArgs;
  const options: AskUserQuestionOption[] = (args.options ?? []).map((o) =>
    typeof o === "string" ? { label: o } : o
  );

  const onResponse = async (response: AskUserQuestionResponse) => {
    const picked =
      typeof response.index === "number" ? options[response.index]?.label : undefined;
    const text = typeof response.text === "string" ? response.text.trim() : "";
    const answer = picked ?? (text || "(no answer)");
    const stamp = `aq-${Date.now()}`;
    const optimistic = [
      { type: "ai", id: `${stamp}-q`, content: args.question },
      { type: "human", id: `${stamp}-a`, content: answer },
    ];
    await submit(null, {
      command: { resume: response },
      optimisticValues: (prev: Record<string, unknown>) => ({
        ...prev,
        messages: [...((prev?.messages as unknown[]) ?? []), ...optimistic],
      }),
    });
  };

  return <AskUserQuestionCard interrupt={interrupt} onResponse={onResponse} className="text-sm" />;
}

export interface BrainstormMessagesViewProps {
  turns: Turn[];
  isStreaming: boolean;
  /** True while the agent is compacting history — shows CompactingIndicator. */
  isCompacting?: boolean;
  totalQuestions?: number;
  /** Reviewable artifacts from the channel; each renders anchored to its turn. */
  artifacts?: Artifact[];
  onApprove?: (artifact: Artifact) => void;
}

/**
 * Pure presentation component for brainstorm messages.
 * Uses Chat.AIMessage for rendering — only adds brainstorm-specific
 * QuestionBadge and IntentButtons around the generic components.
 */
export function BrainstormMessagesView({
  turns,
  isStreaming,
  isCompacting = false,
  artifacts,
  onApprove,
}: BrainstormMessagesViewProps) {
  // Tool-activity bursts, keyed by anchor message id. Derived over the
  // flat stream (human messages break groups, so turn boundaries hold).
  // Hook call stays above the empty-state return.
  const allMessages = turns.flatMap((t) => t.messages);
  const { byMessage, isLive } = useAgentActivity({
    messages: allMessages,
    isStreaming,
  });

  if (turns.length === 0) return null;

  // Anchor each artifact to its first surfacing tool-message (deduped across the
  // whole stream, so a retried/duplicated tool message can't double-render it).
  const artifactsByMessage = assignArtifactsToMessages(allMessages, artifacts);

  return (
    <Chat.Messages.List className="flex-1 min-h-0 py-4 [&>*+*]:mt-6">
      {turns.map((turn) => {
        const lastIndex = turn.messages.length - 1;
        // Follow-on AI messages in the same turn continue the first
        // message's lockup (no repeated avatar/name) — a multi-beat
        // turn reads as one conversational pass.
        let aiHeaderShown = false;

        return (
          <div key={turn.id} className="space-y-6">
            {turn.messages.map((msg, i) => {
              if (msg.type === "human") {
                // A submitted recommendation panel renders as its structured
                // card (the transcript anchor); plain messages fall back.
                return (
                  <Chat.Recommendations.SubmissionMessage
                    key={msg.id}
                    message={msg}
                    fallback={<Chat.UserMessage message={msg} />}
                  />
                );
              }

              // A keyword table renders inline at its create_or_edit_artifact
              // tool-call, so it stays anchored in history. Whether it's
              // read-only is the table's own call (artifact status `completed`
              // once the user responds) — the parent only blocks edits while a
              // run streams.
              if (msg.type === "tool") {
                const anchored = msg.id ? artifactsByMessage.get(msg.id) : undefined;
                if (!anchored || anchored.length === 0) return null;
                return (
                  // Flush-left with the lockup body and tool cards — the
                  // whole turn reads as one left-aligned column.
                  <div key={msg.id}>
                    <ArtifactRenderer
                      artifacts={anchored}
                      onApprove={onApprove ?? (() => {})}
                      disabled={isStreaming}
                      present={keywordPresent}
                    />
                  </div>
                );
              }
              if (msg.type !== "ai") return null;

              const isLastInTurn = turn.isLatest && i === lastIndex;
              const activity = msg.id ? byMessage.get(msg.id) : undefined;
              const continuation = aiHeaderShown;
              if (extractText(msg.content)?.trim()) aiHeaderShown = true;

              // Fragment, not a wrapper div: Chat.AIMessage renders null
              // for messages with nothing visible, and a wrapper would
              // still occupy a row in the turn's space-y stack — a phantom
              // blank line that appears and vanishes mid-stream, bouncing
              // everything below it. The ToolActivity card renders at its
              // burst's anchor message, flush-left with Houston's text.
              return (
                <Fragment key={msg.id}>
                  <Chat.AIMessage
                    message={msg}
                    isStreaming={isLastInTurn && isStreaming}
                    continuation={continuation}
                  />
                  {activity && (
                    <Chat.ToolActivity
                      group={activity}
                      isStreaming={turn.isLatest && isStreaming}
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        );
      })}

      <Chat.QueuedMessages />
      <ActiveAskUserQuestion />
      {isCompacting ? (
        <CompactingIndicator />
      ) : (
        // A live burst's card carries its own spinner — skip the second pulse.
        isStreaming && !isLive && <ThinkingIndicator text="Thinking" />
      )}
      <ChatErrorBanner />
    </Chat.Messages.List>
  );
}

/**
 * Container component for brainstorm messages.
 */
export function BrainstormMessages() {
  const messages = useBrainstormSelector((s) => s.messages);
  const isStreaming = useBrainstormSelector((s) => s.isLoading);
  const isCompacting = useBrainstormIsCompacting();
  const { artifacts, onApprove } = useChatArtifacts();

  const turns = useTurns(messages);

  return (
    <BrainstormMessagesView
      turns={turns}
      isStreaming={isStreaming}
      isCompacting={isCompacting}
      artifacts={artifacts}
      onApprove={onApprove}
    />
  );
}
